import React, { useEffect, useState, createContext, useContext, createElement } from 'react';
import { translations, type Lang, type TranslationKey } from '../i18n/translations';
interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function detectLanguage(): Lang {
  // Check localStorage first
  const saved = localStorage.getItem('lang');
  if (saved === 'en' || saved === 'es') return saved;
  // Auto-detect from browser
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  return browserLang.toLowerCase().startsWith('es') ? 'es' : 'en';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Lang>(detectLanguage);
  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('lang', newLang);
    document.documentElement.lang = newLang;
    document.documentElement.setAttribute('translate', 'no');
  };
  // Set lang attribute on mount to prevent Chrome auto-translate
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.setAttribute('translate', 'no');
    // Add meta tag to prevent Google Translate
    const meta = document.createElement('meta');
    meta.name = 'google';
    meta.content = 'notranslate';
    document.head.appendChild(meta);
  }, []);
  const t = (key: TranslationKey): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry['en'] || key;
  };
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-209">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}